import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, ToggleButtonGroup,
  ToggleButton, Alert, CircularProgress, Chip, Divider, LinearProgress
} from '@mui/material';
import { LockOutlined, CheckCircle, ArrowBack } from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';

const PRICES = { monthly: '£40 / month', annual: '£400 / year' };

// ---------------------------------------------------------------------------
// Inner Stripe form
// ---------------------------------------------------------------------------
interface WallCheckoutFormProps {
  plan: 'monthly' | 'annual';
  onSuccess: () => void;
}

const WallCheckoutForm: React.FC<WallCheckoutFormProps> = ({ plan, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard` },
      redirect: 'if_required',
    });

    if (submitError) {
      setError(submitError.message || 'Payment failed');
      setProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setElementReady(true)} />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!stripe || processing || !elementReady}
        sx={{ mt: 3 }}
      >
        {processing || !elementReady
          ? <CircularProgress size={24} color="inherit" />
          : `Subscribe — ${PRICES[plan]}`}
      </Button>
    </form>
  );
};

// ---------------------------------------------------------------------------
// PaymentWall — full-screen overlay
// ---------------------------------------------------------------------------
const PaymentWall: React.FC = () => {
  const { subscription, refreshSubscriptionStatus, logout, token } = useAuth();
  const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly');
  const [step, setStep] = useState<'select' | 'pay'>('select');
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${config.apiUrl}/api/payments/config`).then(res => {
      setStripePromise(loadStripe(res.data.publishable_key));
    });
  }, []);

  const handleContinue = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    setClientSecret(null);

    try {
      const res = await axios.post(`${config.apiUrl}/api/payments/create-subscription`, { plan }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientSecret(res.data.client_secret);
      setStep('pay');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set up payment');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = () => {
    setStep('select');
    setClientSecret(null);
    setError(null);
  };

  const handleSuccess = async () => {
    await refreshSubscriptionStatus();
  };

  const handleCheckStatus = async () => {
    setSyncLoading(true);
    try {
      await refreshSubscriptionStatus();
    } catch (err: any) {
      setError('Failed to sync payment status. Please try again.');
    } finally {
      setSyncLoading(false);
    }
  };

  const statusMessage = () => {
    const s = subscription?.subscription_status;
    if (s === 'cancelled') return 'Your subscription has been cancelled.';
    if (s === 'past_due') return 'Your last payment failed.';
    if (s === 'trial' && subscription?.trial_expired) return 'Your free trial has ended.';
    return 'A subscription is required to access the app.';
  };

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 9999,
      bgcolor: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2,
    }}>
      <Card sx={{ maxWidth: 500, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockOutlined sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={700}>Subscription required</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>{statusMessage()}</Typography>
            <Button
              size="small"
              onClick={handleCheckStatus}
              disabled={syncLoading}
              sx={{ mt: 1.5 }}
            >
              {syncLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Check Status
            </Button>
          </Box>

          {['Full access to all features', 'AI certificate extraction', 'Expiry reminders & Gmail integration'].map(perk => (
            <Box key={perk} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
              <Typography variant="body2">{perk}</Typography>
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />

          {step === 'select' ? (
            <>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Select plan</Typography>
              <ToggleButtonGroup
                value={plan}
                exclusive
                onChange={(_, val) => val && setPlan(val)}
                fullWidth
                sx={{ mb: 3 }}
              >
                <ToggleButton value="monthly" sx={{ py: 1.5 }}>
                  <Box>
                    <Typography fontWeight={600}>Monthly</Typography>
                    <Typography variant="body2" color="text.secondary">£40 / month</Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="annual" sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>Annual</Typography>
                      <Chip label="Best value" size="small" color="success" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">£400 / year</Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleContinue}
                disabled={loading}
              >
                {loading
                  ? <CircularProgress size={24} color="inherit" />
                  : `Continue with ${plan === 'monthly' ? 'Monthly' : 'Annual'} — ${PRICES[plan]}`}
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {plan === 'monthly' ? 'Monthly — £40 / month' : 'Annual — £400 / year'}
                </Typography>
                <Button size="small" startIcon={<ArrowBack />} onClick={handleChangePlan}>
                  Change
                </Button>
              </Box>

              {loading ? (
                <>
                  <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
                  <Box sx={{
                    bgcolor: 'grey.50', borderRadius: 1, p: 3, textAlign: 'center',
                    border: '1px solid', borderColor: 'grey.200',
                  }}>
                    <Typography variant="body2" color="text.secondary">Preparing payment form…</Typography>
                  </Box>
                </>
              ) : clientSecret && stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
                  <WallCheckoutForm plan={plan} onSuccess={handleSuccess} />
                </Elements>
              ) : null}
            </>
          )}

          <Button variant="text" fullWidth sx={{ mt: 2, color: 'text.secondary' }} onClick={logout}>
            Log out
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentWall;
